import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ClipboardCheck,
  CheckCircle,
  Camera,
  FileText,
  Users,
  Smartphone,
  Shield,
  Zap,
  Download,
  Clock,
  Building2,
  Wifi,
  WifiOff,
  PenTool,
  FolderOpen,
  Star,
  ArrowRight
} from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-slate-800 tracking-tight">
                QA BUILDER PRO
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                size="sm"
                className="hidden sm:flex items-center gap-2 text-primary border-primary/30 hover:bg-primary/5"
                onClick={() => {
                  if (window.deferredPrompt) {
                    window.deferredPrompt.prompt();
                    window.deferredPrompt.userChoice.then((choiceResult) => {
                      window.deferredPrompt = null;
                    });
                  } else {
                    alert('To install: Click the browser menu and select "Install App" or "Add to Home Screen"');
                  }
                }}
                data-testid="landing-download-btn"
              >
                <Download className="h-4 w-4" />
                Install App
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-primary">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary hover:bg-primary/90 shadow-md">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-primary/5 py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/10 px-4 py-1.5">
              <Star className="w-3.5 h-3.5 mr-1.5" />
              Trusted by Construction Professionals
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
              DIGITAL QA FORMS FOR{' '}
              <span className="text-primary">CONSTRUCTION</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
              Create professional inspection checklists, capture photo evidence, collect digital signatures, 
              and generate PDF reports — all from one powerful platform that works offline on-site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all h-12 px-8 text-base">
                  Start 7-Day Free Trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="h-12 px-8 text-base">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              No credit card required • Cancel anytime
            </p>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-orange-100/50 rounded-full blur-3xl"></div>
      </section>

      {/* What is QA Builder Pro */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              WHAT IS QA BUILDER PRO?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A complete quality assurance solution designed specifically for the construction industry
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-800 mb-1">Digital Inspection Forms</h3>
                  <p className="text-slate-600">
                    Replace paper checklists with digital forms. Create custom QA inspections for any trade — 
                    plumbing, electrical, drylining, roofing, and more.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-800 mb-1">Photo Documentation</h3>
                  <p className="text-slate-600">
                    Capture and attach photos directly to inspection items. Add descriptions and comments 
                    to create a complete visual record of your work.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-800 mb-1">Team Collaboration</h3>
                  <p className="text-slate-600">
                    Work together with your team. Admins manage the company, managers review and approve forms, 
                    and operatives submit their inspections.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl p-8 shadow-inner">
              <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-100 text-green-700">Approved</Badge>
                  <span className="text-sm text-slate-500">Today</span>
                </div>
                <h4 className="font-semibold text-slate-800">Plumbing 1st Fix - Block A, Floor 3</h4>
                <div className="flex gap-2">
                  <div className="w-16 h-16 bg-slate-200 rounded-lg"></div>
                  <div className="w-16 h-16 bg-slate-200 rounded-lg"></div>
                  <div className="w-16 h-16 bg-slate-200 rounded-lg"></div>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>12/12 items passed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              KEY FEATURES
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to manage quality assurance on your construction projects
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<ClipboardCheck className="w-6 h-6" />}
              iconBg="bg-primary/10"
              iconColor="text-primary"
              title="Custom Checklists"
              description="Create inspection checklists with up to 25 items per form. Define your own requirements for any trade or project."
            />
            <FeatureCard
              icon={<Camera className="w-6 h-6" />}
              iconBg="bg-green-100"
              iconColor="text-green-600"
              title="Photo Evidence"
              description="Upload multiple photos per form. Add descriptions and admin comments for complete documentation."
            />
            <FeatureCard
              icon={<PenTool className="w-6 h-6" />}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              title="Digital Signatures"
              description="Capture signatures directly on the form. Perfect for sign-offs and approvals."
            />
            <FeatureCard
              icon={<FileText className="w-6 h-6" />}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              title="PDF Export"
              description="Generate professional PDF reports with your company logo watermark. Share via email instantly."
            />
            <FeatureCard
              icon={<WifiOff className="w-6 h-6" />}
              iconBg="bg-orange-100"
              iconColor="text-orange-600"
              title="Offline Mode"
              description="Works without internet. Create and save forms on-site, sync automatically when you're back online."
            />
            <FeatureCard
              icon={<FolderOpen className="w-6 h-6" />}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              title="Templates"
              description="Save forms as templates for reuse. Import and export templates to share with your team."
            />
            <FeatureCard
              icon={<Shield className="w-6 h-6" />}
              iconBg="bg-red-100"
              iconColor="text-red-600"
              title="Approval Workflow"
              description="Submit forms for review. Managers can approve, reject, or return forms with comments."
            />
            <FeatureCard
              icon={<Building2 className="w-6 h-6" />}
              iconBg="bg-cyan-100"
              iconColor="text-cyan-600"
              title="Multi-Site Support"
              description="Manage multiple construction sites and jobs under one company account."
            />
            <FeatureCard
              icon={<Download className="w-6 h-6" />}
              iconBg="bg-teal-100"
              iconColor="text-teal-600"
              title="Export Photos"
              description="Download all form photos as a ZIP bundle for easy archiving and sharing."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              HOW IT WORKS
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get started in minutes with our simple three-step process
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">Create Your Account</h3>
              <p className="text-slate-600">
                Register your company and invite your team. Set up as Admin, Manager, or Operative based on your role.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">Create Inspection Forms</h3>
              <p className="text-slate-600">
                Select a trade, add your checklist items, upload photos, and capture signatures. Works offline too.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">Submit & Export</h3>
              <p className="text-slate-600">
                Submit forms for approval. Once approved, export as PDF with your company branding and share with clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trades Supported */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              TRADES SUPPORTED
            </h2>
            <p className="text-lg text-slate-600">
              Pre-built templates for all major construction trades
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {['Plumbing', 'Electrics', 'Drylining', 'Painting', 'Roofing', 'HVAC', 'Carpentry', 'Flooring', 'Fire Safety', 'Glazing', 'Steel Workers', 'Lift Engineers', 'Decorating'].map((trade) => (
              <Badge key={trade} variant="secondary" className="px-4 py-2 text-sm bg-white shadow-sm">
                {trade}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            SIMPLE, TRANSPARENT PRICING
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start with a 7-day free trial. No credit card required.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <div className="bg-white/10 backdrop-blur rounded-xl px-8 py-6 text-center">
              <p className="text-white/70 text-sm mb-1">Monthly</p>
              <p className="text-4xl font-bold text-white">£3</p>
              <p className="text-white/70 text-sm">per month</p>
            </div>
            <span className="text-white/50 text-xl">or</span>
            <div className="bg-white/20 backdrop-blur rounded-xl px-8 py-6 text-center border-2 border-white/30">
              <p className="text-white/70 text-sm mb-1">Lifetime</p>
              <p className="text-4xl font-bold text-white">£10</p>
              <p className="text-white/70 text-sm">one-time payment</p>
            </div>
          </div>
          
          <Link to="/register">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg h-12 px-8 text-base font-semibold">
              Start Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold tracking-tight">QA BUILDER PRO</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            © 2025 QA Builder Pro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, iconBg, iconColor, title, description }) {
  return (
    <Card className="border-slate-200 hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className={`w-12 h-12 rounded-lg ${iconBg} flex items-center justify-center mb-4`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <h3 className="font-semibold text-lg text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}
